import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import brands from "../data/brands.json";
import styles from "./HeroSlider.module.css";

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index) => {
      if (animating) return;
      setAnimating(true);
      setTimeout(() => setAnimating(false), 700);
      setCurrent(index);
    },
    [animating],
  );

  const next = useCallback(
    () => goTo((current + 1) % brands.length),
    [current, goTo],
  );
  const prev = useCallback(
    () => goTo((current - 1 + brands.length) % brands.length),
    [current, goTo],
  );

  // Auto-scroll
  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const brand = brands[current];

  return (
    <section className={styles.hero}>
      {/* Background slides */}
      {brands.map((b, i) => (
        <div
          key={b.id}
          className={`${styles.slide} ${i === current ? styles.active : ""}`}
          style={{ backgroundImage: `url(${b.heroImage})` }}
        />
      ))}

      {/* Overlays */}
      <div className={styles.gradientOverlay} />
      <div className={styles.patternOverlay} />

      {/* Content */}
      <div className={styles.content}>
        <div
          className={`${styles.inner} ${animating ? styles.fadeIn : ""}`}
          key={current}
        >
          <p className={styles.eyebrow}>
            {brand.isFeatured ? "⭐ Flagship Brand" : "Featured Brand"}
          </p>
          <h1 className={styles.title}>{brand.name}</h1>
          <p className={styles.tagline}>{brand.tagline}</p>
          <p className={styles.brief}>{brand.brief}</p>
          <div className={styles.actions}>
            <Link to={`/brands/${brand.slug}`} className={styles.primaryBtn}>
              Explore Products <ArrowRight size={16} />
            </Link>
            <button
              className={styles.ghostBtn}
              onClick={() => {
                const el = document.getElementById("brands");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              All Brands
            </button>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        className={`${styles.arrow} ${styles.arrowLeft}`}
        onClick={prev}
        aria-label="Previous"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        className={`${styles.arrow} ${styles.arrowRight}`}
        onClick={next}
        aria-label="Next"
      >
        <ChevronRight size={24} />
      </button>

      {/* Dots */}
      <div className={styles.dots}>
        {brands.map((_, i) => (
          <button
            key={i}
            className={`${styles.dot} ${i === current ? styles.dotActive : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className={styles.progressBar} key={`bar-${current}`} />
    </section>
  );
}
